import type { ProductAttributeResolver } from "@/modules/offers/types/createOffer"
import type {
  FormattedProductAttribute,
  ProductModerationAttribute
} from "@/modules/offers/types/offers"

import { getOrCreateAttributeValue } from "./manualAttributeValues"

const formatAttribute = ({
  code,
  value,
  is_required = false
}: ProductModerationAttribute): FormattedProductAttribute => {
  return {
    code,
    value: value.map((item) => item?.code).filter(Boolean),
    is_required
  }
}

export const formatAttributesForRequest = (
  attributes: Array<ProductModerationAttribute>,
  freeFormAttributes: Array<ProductModerationAttribute> = []
): Array<FormattedProductAttribute> => {
  return [...attributes, ...freeFormAttributes].map(formatAttribute)
}

export const hasEmptyRequiredAttributes = (attributes: Array<FormattedProductAttribute>) => {
  return attributes.some((attr) => attr.is_required && attr.value.length === 0)
}

export const hasAttributeValue = (attribute: ProductModerationAttribute) =>
  attribute.value.some((value) => {
    if (attribute.is_defined_list) return !!value?.code

    return typeof value?.value === "string" ? value.value.trim() !== "" : value?.value != null
  })

export const hasIncompleteFreeFormAttributes = (attributes: ProductModerationAttribute[]) =>
  attributes.some((attribute) => !attribute.code || !hasAttributeValue(attribute))

export const createProductAttributeResolver = (): ProductAttributeResolver => {
  const manualValues = new Map<string, ProductModerationAttribute["value"]>()
  const resolveAttributes = async (attributes: ProductModerationAttribute[]) => {
    const resolved: ProductModerationAttribute[] = []

    for (const attribute of attributes) {
      if (attribute.is_defined_list) {
        resolved.push(attribute)
        continue
      }

      const attrValue = attribute.value?.[0]
      const hasValue = attrValue?.value != null && String(attrValue.value).trim() !== ""
      const cacheKey = JSON.stringify([attribute.code, attrValue])
      let value = hasValue ? manualValues.get(cacheKey) : []

      if (!value) {
        value = await getOrCreateAttributeValue({ attribute, attrValue })
        manualValues.set(cacheKey, value)
      }

      resolved.push({ ...attribute, value })
    }

    return formatAttributesForRequest(resolved)
  }
  return resolveAttributes
}
